/**
 * זיהוי המכשיר והדפדפן — כדי שההנחיות (התקנה, התראות) יתאימו למה שהמשתמש באמת צריך לעשות בטלפון/טאבלט/מחשב שלו.
 * הזיהוי לפי User-Agent הוא הערכה בלבד: משמש להצגת עזרה מתאימה, לעולם לא כדי לחסום תכונה (התכונות נבדקות לפי יכולת).
 */

/** האפליקציה רצה כמותקנת (PWA במסך הבית) ולא בלשונית דפדפן */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  return isIos() && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export type OsFamily = 'ios' | 'android' | 'windows' | 'mac' | 'other';
export type BrowserFamily = 'chrome' | 'safari' | 'firefox' | 'samsung' | 'edge' | 'opera' | 'xiaomi' | 'other';
export type DeviceClass = 'phone' | 'tablet' | 'desktop';
/** יצרן אנדרואיד עם ניהול סוללה אגרסיבי שחוסם התראות ברקע אלא אם מבטלים לאפליקציה את ההגבלה */
export type OemFamily = 'samsung' | 'xiaomi' | 'huawei' | 'other-oem' | null;

export interface Environment {
  os: OsFamily;
  browser: BrowserFamily;
  device: DeviceClass;
  standalone: boolean;
  /** דפדפן מובנה של אפליקציה אחרת (וואטסאפ, אינסטגרם, פייסבוק...) או WebView — אין בו התקנה והתראות */
  inAppBrowser: boolean;
  oem: OemFamily;
  /** גרסת iOS (למשל 16.4), או null אם אינו iOS / לא ידוע */
  iosVersion: number | null;
}

const IN_APP_RE = /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|BytedanceWebview|musical_ly|Twitter|Pinterest|LinkedInApp|GSA\/|WhatsApp|Telegram/i;

function detectOem(ua: string): OemFamily {
  if (/SM-|SamsungBrowser|Samsung|GT-I|SGH-/i.test(ua)) return 'samsung';
  if (/Xiaomi|Redmi|POCO|MIUI|MiuiBrowser|\bMi \d|M\d{4}[A-Z0-9]{3,}/i.test(ua)) return 'xiaomi';
  if (/HUAWEI|HONOR|HMSCore|\bVOG-|\bELS-|\bANA-/i.test(ua)) return 'huawei';
  if (/OPPO|Realme|OnePlus|\bCPH\d|vivo|\bV\d{4}/i.test(ua)) return 'other-oem';
  return null;
}

/** ניתוח User-Agent לתיאור סביבה. פונקציה טהורה — נבדקת על מחרוזות UA אמיתיות */
export function parseEnvironment(ua: string, ctx: { maxTouchPoints?: number; standalone?: boolean } = {}): Environment {
  const touch = ctx.maxTouchPoints ?? 0;
  const standalone = ctx.standalone ?? false;
  const iPhoneOrPod = /iPhone|iPod/.test(ua);
  const iPadClassic = /iPad/.test(ua);
  const iPadDesktopUa = /Macintosh/.test(ua) && touch > 1; // iPadOS מזדהה כ-Mac
  const ios = iPhoneOrPod || iPadClassic || iPadDesktopUa;
  const android = /Android/.test(ua);
  const os: OsFamily = ios ? 'ios' : android ? 'android' : /Windows/.test(ua) ? 'windows' : /Macintosh/.test(ua) ? 'mac' : 'other';

  let browser: BrowserFamily = 'other';
  if (/SamsungBrowser/.test(ua)) browser = 'samsung';
  else if (/MiuiBrowser|XiaoMi\//i.test(ua)) browser = 'xiaomi';
  else if (/Edg\/|EdgA\/|EdgiOS/.test(ua)) browser = 'edge';
  else if (/OPR\/|OPiOS|Opera/.test(ua)) browser = 'opera';
  else if (/Firefox|FxiOS/.test(ua)) browser = 'firefox';
  else if (/Chrome|CriOS/.test(ua)) browser = 'chrome';
  else if (/Safari/.test(ua)) browser = 'safari';
  else if (ios) browser = 'safari'; // אפליקציה מותקנת/WebView ב-iOS: אותו מנוע, בלי הטוקן "Safari"

  let device: DeviceClass = 'desktop';
  if (iPhoneOrPod) device = 'phone';
  else if (iPadClassic || iPadDesktopUa) device = 'tablet';
  else if (android) device = /Mobile/.test(ua) ? 'phone' : 'tablet';

  // ב-iOS גם אפליקציה מותקנת וגם WebView של אפליקציה אחרת מזדהים בלי "Safari/" — ההבדל: מותקנת היא standalone.
  const iosWebView = ios && !standalone && !/Safari\//.test(ua);
  const androidWebView = android && /;\s*wv\)/.test(ua);
  const inAppBrowser = IN_APP_RE.test(ua) || iosWebView || androidWebView;

  let iosVersion: number | null = null;
  if (ios) {
    const m = /OS (\d+)[_.](\d+)/.exec(ua) ?? /Version\/(\d+)\.(\d+)/.exec(ua);
    if (m) iosVersion = Number(`${m[1]}.${m[2]}`);
  }

  return { os, browser, device, standalone, inAppBrowser, oem: android ? detectOem(ua) : null, iosVersion };
}

/** הסביבה הנוכחית של הדפדפן הזה */
export function detectEnvironment(): Environment {
  return parseEnvironment(window.navigator.userAgent, { maxTouchPoints: navigator.maxTouchPoints, standalone: isStandalone() });
}

export interface HelpSection {
  heading: string;
  steps: string[];
}

/** הנחיות התקנה והתראות שמתאימות למכשיר — מוצגות תחת "לא מגיעות התראות?" */
export function notificationHelp(env: Environment): HelpSection {
  if (env.inAppBrowser) {
    return {
      heading: 'אתם בתוך אפליקציה אחרת (וואטסאפ, אינסטגרם וכד׳)',
      steps: [
        'בדפדפן המובנה של אפליקציה אחרת אי אפשר להתקין את האפליקציה ולקבל התראות.',
        'לחצו על ⋮ (או על סמל השיתוף/המצפן) ← "פתח בדפדפן", ופתחו את הקישור ב-Chrome או ב-Safari.',
        'משם אפשר להתקין את האפליקציה למסך הבית ולהפעיל התראות.',
      ],
    };
  }
  if (env.os === 'ios') {
    return {
      heading: env.device === 'tablet' ? 'התראות באייפד' : 'התראות באייפון',
      steps: [
        'התראות עובדות רק מהאפליקציה שנוספה למסך הבית, ורק ב-iOS 16.4 ומעלה (הגדרות ← כללי ← עדכון תוכנה).',
        'ב-Safari: כפתור השיתוף ← "הוסף למסך הבית", ופתחו את האפליקציה מהסמל שנוצר.',
        'לחצו "הפעל התראות" ואשרו כשמופיעה בקשת ההרשאה.',
        'אם לא מגיעות: הגדרות ← התראות ← (שם האפליקציה) — ודאו שמאופשר. אם מופעל "לא להפריע"/Focus, הוסיפו את האפליקציה לרשימת המותרות.',
      ],
    };
  }
  if (env.os === 'android') {
    const steps = [
      'הגדרות הטלפון ← אפליקציות ← הדפדפן (Chrome / Samsung Internet) ← התראות — מאופשרות.',
      'באותו מקום: סוללה ← "ללא הגבלה" (Unrestricted), כדי שהטלפון לא יעצור התראות כשהמסך נעול.',
      'בתוך הדפדפן: הגדרות ← הגדרות אתר ← התראות — ודאו שהאתר מורשה.',
    ];
    if (env.oem === 'samsung') {
      steps.push('סמסונג: הגדרות ← סוללה ← מגבלות שימוש ברקע — הוציאו את הדפדפן מ"אפליקציות במצב שינה" ומ"שינה עמוקה".');
    } else if (env.oem === 'xiaomi') {
      steps.push('שיאומי (MIUI): הגדרות ← אפליקציות ← ניהול אפליקציות ← הדפדפן ← "הפעלה אוטומטית" (Autostart) — מופעל, וחיסכון בסוללה — "ללא הגבלות".');
    } else if (env.oem === 'huawei') {
      steps.push('Huawei/Honor: ללא שירותי Google במכשיר דפדפנים לא יכולים לקבל התראות אתר — האפליקציה תתעדכן כשהיא פתוחה.');
    } else if (env.oem === 'other-oem') {
      steps.push('באפליקציית ניהול הסוללה של היצרן (אופו/ריאלמי/וואן-פלאס/ויוו) אפשרו לדפדפן לרוץ ברקע והפעילו לו "הפעלה אוטומטית".');
    }
    return { heading: 'התראות באנדרואיד', steps };
  }
  return {
    heading: env.os === 'mac' ? 'התראות במק' : 'התראות במחשב',
    steps: [
      env.os === 'windows'
        ? 'הגדרות Windows ← מערכת ← התראות — ודאו שההתראות מופעלות והדפדפן מופיע ברשימה.'
        : 'הגדרות המערכת ← התראות — ודאו שהדפדפן מורשה להציג התראות.',
      'אם מופעל "עזר מיקוד" / "לא להפריע" / Focus — כבו אותו כדי לראות התראות.',
      'בדפדפן: סמל המנעול ליד כתובת האתר ← התראות ← "אפשר".',
    ],
  };
}
